import { defineStore } from 'pinia'
import { ref } from 'vue'
import zh_tran from '@/utils/zh_tran'

export const useLangStore = defineStore('lang', () => {
  // 从localStorage获取存储的语言，如果没有则默认为simplified
  const savedLang = localStorage.getItem('lang') || 'simplified'
  const currentLang = ref(savedLang)
  let observer = null
  let debounceTimer = null
  
  // 判断元素是否为表单元素或其子元素
  const isFormElement = (node) => {
    const formTags = ['INPUT', 'TEXTAREA', 'SELECT']
    let element = node.nodeType === 3 ? node.parentElement : node

    while (element) {
      if (formTags.includes(element.tagName)) {
        return true
      }
      element = element.parentElement
    }
    return false
  }

  // 获取需要转换的文本节点
  const getTextNodesToTranslate = (element) => {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // 排除表单元素内的所有文本
          if (isFormElement(node)) {
            return NodeFilter.FILTER_REJECT
          }
          // 排除空文本节点
          if (!node.textContent.trim()) {
            return NodeFilter.FILTER_REJECT
          }
          // 排除script和style标签内的文本
          if (node.parentElement?.tagName === 'SCRIPT' || node.parentElement?.tagName === 'STYLE') {
            return NodeFilter.FILTER_REJECT
          }
          return NodeFilter.FILTER_ACCEPT
        }
      }
    )

    const textNodes = []
    let node
    while (node = walker.nextNode()) {
      textNodes.push(node)
    }
    return textNodes
  }

  // 转换文本节点
  const translateTextNodes = (nodes) => {
    if (!nodes || nodes.length === 0) return

    // 确保设置了正确的转换模式
    window.zh_choose = currentLang.value === 'traditional' ? 't' : 's'

    nodes.forEach(node => {
      if (node.nodeType === 3 && node.data.trim()) {
        node.data = String.prototype.tran.call(node.data)
      }
    })
  }

  // 转换整个页面
  const translateFullPage = () => {
    // 先使用 zh_tran 进行全局转换
    const mode = currentLang.value === 'traditional' ? 't' : 's'
    zh_tran(mode)

    // 然后处理可能遗漏的动态内容
    const appElement = document.querySelector('#app')
    if (appElement) {
      const textNodes = getTextNodesToTranslate(appElement)
      translateTextNodes(textNodes)
    }
  }

  // 创建 MutationObserver
  const createObserver = () => {
    observer = new MutationObserver((mutations) => {
      // 使用防抖处理，避免频繁触发
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      debounceTimer = setTimeout(() => {
        const addedNodes = []
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              // 如果是元素节点，获取其中的文本节点
              if (node.nodeType === 1) {
                const textNodes = getTextNodesToTranslate(node)
                addedNodes.push(...textNodes)
              }
              // 如果是文本节点且不在表单元素内
              else if (node.nodeType === 3 && !isFormElement(node)) {
                addedNodes.push(node)
              }
            })
          }
        })

        if (addedNodes.length > 0) {
          translateTextNodes(addedNodes)
        }
      }, 100)
    })

    // 配置观察选项
    const config = {
      childList: true,
      subtree: true,
      characterData: true
    }

    // 开始观察整个应用
    const appElement = document.querySelector('#app')
    if (appElement) {
      observer.observe(appElement, config)
    }
  }

  // 切换语言
  const switchLang = (lang) => {
    if (currentLang.value === lang) return
    
    currentLang.value = lang
    localStorage.setItem('lang', lang)
    
    // 执行完整的页面转换
    translateFullPage()
  }

  // 初始化语言设置
  const initLang = () => {
    // 执行完整的页面转换
    translateFullPage()
    
    // 创建观察者
    createObserver()
  }

  // 清理函数
  const cleanup = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  return {
    currentLang,
    switchLang,
    initLang,
    cleanup,
    translateFullPage // 导出这个方法以供路由切换时使用
  }
}) 